import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Button, Card, Input } from '../../components/ui'
import { Footer } from '../../components/Footer'
import { GhostMark } from '../../components/GhostMark'
import { IconCheck } from '../../components/icons'
import { useStore } from '../../lib/store'
import { inviteInfo, type InviteInfo } from '../../lib/teamApi'
import './auth.css'

// The landing page for an emailed invite link (/accept-invite?token=…). It looks
// up the pending invite (email + org + role are fixed by whoever invited you),
// you confirm your name and choose a password, and the sidecar mints your
// recruiter login + signs you straight into the console.

const ROLE_BLURB: Record<string, string> = {
  admin: 'Admin — manage the team & billing',
  recruiter: 'Recruiter — full pipeline access',
  viewer: 'Viewer — read-only access',
  owner: 'Owner',
}

export function AcceptInvite() {
  const { acceptInvite } = useStore()
  const nav = useNavigate()
  const [params] = useSearchParams()
  const token = params.get('token') || ''

  const [info, setInfo] = useState<InviteInfo | null>(null)
  const [loadError, setLoadError] = useState('')
  const [name, setName] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  // Resolve the invite on mount. A missing/expired token shows a dead-end message.
  useEffect(() => {
    if (!token) {
      setLoadError('This invite link is missing its token. Ask your admin to resend it.')
      return
    }
    let cancelled = false
    inviteInfo(token)
      .then((i) => {
        if (cancelled) return
        setInfo(i)
        setName(i.name || '')
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err instanceof Error ? err.message : 'This invite link is invalid or expired.')
      })
    return () => {
      cancelled = true
    }
  }, [token])

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    if (!name.trim()) return setError('Add your name.')
    if (password.length < 8) return setError('Use at least 8 characters for your password.')
    setLoading(true)
    try {
      await acceptInvite(token, name.trim(), password)
      nav('/candidates')
    } catch (err) {
      setLoading(false)
      setError(err instanceof Error ? err.message : 'Could not accept the invite. Try again.')
    }
  }

  return (
    <div className="auth">
      <Card className="auth__card">
        <div className="auth__logo">
          <GhostMark size={64} glow breathing />
        </div>

        {loadError ? (
          <>
            <h1 className="auth__title">Invite unavailable</h1>
            <p className="auth__sub">{loadError}</p>
            <p className="auth__foot">
              Have an account? <Link to="/login">Sign in</Link>
            </p>
          </>
        ) : !info ? (
          <p className="auth__sub" style={{ textAlign: 'center' }}>Loading your invite…</p>
        ) : (
          <>
            <h1 className="auth__title">Join {info.company}</h1>
            <p className="auth__sub">You’ve been invited to {info.company}’s verified hiring pipeline on Vouchr.</p>

            <form className="auth__form" onSubmit={onSubmit} noValidate>
              <div className="company-chip">
                <span className="company-chip__icon">{info.company.slice(0, 1)}</span>
                <span className="company-chip__text">
                  <b>{info.email}</b>
                  <small>{ROLE_BLURB[info.role] || info.role}</small>
                </span>
                <span style={{ marginLeft: 'auto', color: 'var(--cred-live)', display: 'flex' }}>
                  <IconCheck size={16} />
                </span>
              </div>

              <Input
                id="accept-name"
                label="Your name"
                placeholder="Jordan Rivera"
                autoComplete="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />

              <Input
                id="accept-password"
                label="Choose a password"
                type="password"
                autoComplete="new-password"
                placeholder="At least 8 characters"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value)
                  if (error) setError('')
                }}
                error={error || undefined}
              />

              <Button type="submit" variant="primary" size="lg" block loading={loading}>
                Join {info.company}
              </Button>

              <p className="auth__note">
                By joining you agree to Vouchr’s Terms. You can add a passkey later in Settings.
              </p>
            </form>
          </>
        )}
      </Card>
      <Footer />
    </div>
  )
}
